import { useCallback, useEffect, useMemo, useState } from "react";
import {
	CURRENCY_EUR,
	CurrencyInfo,
	CurrencyList,
	getCurrencyList,
} from "../api/currency";

/**
 * Module-level cache of the one request this hook makes.
 *
 * Every `DualMoney`, badge and balance card calls this hook, so a single screen used to fire the
 * same `GET /api/Currency/list` five or more times. Worse than the waste: each copy started at
 * `dualCurrencyEnabled: false`, so EUR figures blinked off for a frame at different moments across
 * the page. Caching the promise gives every consumer the same answer at the same time.
 *
 * The list only changes when an admin edits the rates, and that screen (Arrangements › Currencies)
 * calls `reloadCurrencyList` after a save. Screens that mount afterwards read the new rates; one
 * already open keeps the rates it loaded with.
 */
let currencyListPromise: Promise<CurrencyList | null> | null = null;
let currencyListCache: CurrencyList | null = null;

const loadCurrencyList = () => {
	if (!currencyListPromise) {
		currencyListPromise = getCurrencyList().then((result) => {
			currencyListCache = result;
			return result;
		});
	}
	return currencyListPromise;
};

/** Fetches the list again, after the rates were edited. */
export const reloadCurrencyList = () => {
	currencyListPromise = null;
	return loadCurrencyList();
};

/**
 * Loads `GET /api/Currency/list` once and exposes the bits pages actually need.
 *
 * `dualCurrencyEnabled` is the gate: where it is false there is exactly one currency, the API
 * ignores any `currencyId` sent to it, and no selector should be rendered.
 */
const useCurrencies = () => {
	// Seed from the cache so a hook mounting after the first fetch has resolved never renders a
	// single-currency frame before flipping to dual.
	const [data, setData] = useState<CurrencyList | null>(currencyListCache);
	const [loading, setLoading] = useState(currencyListCache === null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const result = await loadCurrencyList();
			if (!cancelled) {
				setData(result);
				setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// Memoised so `rateOf` and `convert` keep a stable identity — callers put them in dependency
	// arrays, and a fresh `[]` on every render made those memos never hold.
	const currencies: CurrencyInfo[] = useMemo(
		() => data?.currencies ?? [],
		[data]
	);

	/**
	 * The currencies an order may actually be registered in: this deployment's own, plus EUR.
	 *
	 * NOT the raw list above. `GET Currency/list` deliberately returns ALL currency rows so that
	 * `convert()` can resolve any pair, so a selector bound straight to it offers Albanian
	 * operators the Macedonian denar — a state the API rejects and every downstream currency split
	 * would silently bucket as local currency.
	 *
	 * Mirrors the server's own rule, `ICurrencyResolver.GetSupportedCurrencyIdsAsync`. It lives in
	 * the hook rather than in a screen because there are now two screens that choose a currency —
	 * Register order and the order-details correction — and a second copy of this filter is a
	 * second thing to forget. Collapses to one entry off Albania, where `CurrencySelectField`
	 * renders nothing anyway.
	 */
	const selectableCurrencies = useMemo(() => {
		const defaultCurrencyId = data?.defaultCurrencyId ?? 0;
		const allowed = [defaultCurrencyId, CURRENCY_EUR].filter(Boolean);
		return Array.from(new Set(allowed))
			.map((id) => currencies.find((c) => c.id === id))
			.filter((c): c is CurrencyInfo => Boolean(c));
	}, [currencies, data?.defaultCurrencyId]);

	/** The currency's rate. 1 when unknown, i.e. "no conversion". */
	const rateOf = useCallback(
		(currencyId?: number | null) =>
			currencies.find((c) => c.id === currencyId)?.rate ?? 1,
		[currencies]
	);

	/**
	 * Convert between two currencies via the deployment's own currency, which is the pivot every
	 * rate is quoted against (its own rate is 1.0). On PostmanAL that is LEK, with EUR at 0.01041
	 * — so `amount / from.rate` lands in LEK and `* to.rate` leaves it in the target currency.
	 *
	 * Returns null when either currency is unknown, so callers can hide the converted figure
	 * rather than show a wrong one.
	 */
	const convert = useCallback(
		(
			amount: number,
			fromCurrencyId?: number | null,
			toCurrencyId?: number | null
		) => {
			if (!Number.isFinite(amount)) return null;
			const from = currencies.find((c) => c.id === fromCurrencyId);
			const to = currencies.find((c) => c.id === toCurrencyId);
			if (!from || !to || !from.rate) return null;
			return (amount / from.rate) * to.rate;
		},
		[currencies]
	);

	return {
		loading,
		currencies,
		selectableCurrencies,
		countryId: data?.countryId ?? 0,
		dualCurrencyEnabled: data?.dualCurrencyEnabled ?? false,
		defaultCurrencyId: data?.defaultCurrencyId ?? 0,
		rateOf,
		convert,
	};
};

export default useCurrencies;
